import { useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { ContactList } from '../components/ContactList';
import { ContactForm } from '../components/ContactForm';
import { CategoryModal } from '../components/CategoryModal';
import { CSVImportModal } from '../components/CSVImportModal';
import { BulkEditModal } from '../components/BulkEditModal';
import { ChatwootSyncModal } from '../components/ChatwootSyncModal';
import { PerfexImportModal } from '../components/PerfexImportModal';
import { SearchAndFilters } from '../components/SearchAndFilters';
import { Pagination } from '../components/Pagination';
import { Header } from '../components/Header';
import { Contact } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isChatwootSyncModalOpen, setIsChatwootSyncModalOpen] = useState(false);
  const [isPerfexImportModalOpen, setIsPerfexImportModalOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [categoriesVersion, setCategoriesVersion] = useState(0);
  const { user } = useAuth();

  // Confirmation Dialog State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialogConfig({
      title,
      message,
      onConfirm,
      type
    });
    setConfirmDialogOpen(true);
  };

  const pageSize = 30;

  const { contacts, total, totalPages, loading, error, refresh, deleteContact } = useContacts({
    search: search || undefined,
    tag: selectedCategory || undefined,
    page: currentPage,
    pageSize,
  });

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
    refresh();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
  };

  const handleNewContact = () => {
    setEditingContact(undefined);
    setIsFormOpen(true);
  };

  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setCategoriesVersion((prev) => prev + 1);
  };

  const handleOpenCSVImportModal = () => {
    setIsCSVImportModalOpen(true);
  };

  const handleCloseCSVImportModal = () => {
    setIsCSVImportModalOpen(false);
  };

  const handleCSVImportSuccess = () => {
    refresh();
    setIsCSVImportModalOpen(false);
  };

  const handleToggleSelectContact = (contactId: string) => {
    setSelectedContactIds((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = () => {
    if (selectedContactIds.length === contacts.length) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(contacts.map((c) => c.id));
    }
  };

  const handleOpenBulkEdit = () => {
    setIsBulkEditModalOpen(true);
  };

  const handleCloseBulkEdit = () => {
    setIsBulkEditModalOpen(false);
  };

  const handleBulkEditSuccess = () => {
    setSelectedContactIds([]);
    setIsBulkEditModalOpen(false);
    refresh();
  };

  const handleBulkDelete = async () => {
    if (!selectedContactIds.length) return;

    showConfirm(
      'Excluir Contatos',
      `Tem certeza que deseja excluir ${selectedContactIds.length} contato(s)? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch('/api/contatos/bulk/delete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ contactIds: selectedContactIds })
          });

          if (response.ok) {
            toast.success(`${selectedContactIds.length} contatos excluídos com sucesso`);
            setSelectedContactIds([]);
            refresh();
          } else {
            const error = await response.json();
            toast.error(error.error || 'Erro ao excluir contatos');
          }
        } catch (error) {
          console.error('Erro ao excluir contatos em massa:', error);
          toast.error('Erro ao excluir contatos');
        }
      }
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-6 bg-white/5 p-12 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-2">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">Erro ao carregar</h1>
          <p className="text-gray-400 text-sm font-medium leading-relaxed">{error}</p>
          <button
            onClick={refresh}
            className="w-full px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]">
      <Header
        title="Contatos"
        subtitle={
          selectedContactIds.length > 0
            ? `${selectedContactIds.length} contato(s) selecionado(s)`
            : `${total} contatos cadastrados`
        }
        compact
        className="rounded-b-[2rem]"
        actions={
          <div className="flex items-center gap-4 relative z-50">
            {selectedContactIds.length > 0 ? (
              <>
                <button
                  onClick={handleOpenBulkEdit}
                  className="px-6 py-3 bg-white/5 text-orange-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-white/10 transition-all border border-orange-500/20 font-oswald"
                  aria-label="Editar contatos selecionados"
                >
                  Editar Selecionados ({selectedContactIds.length})
                </button>
                {/* Admin Only Bulk Delete */}
                {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                  <button
                    onClick={handleBulkDelete}
                    className="px-6 py-3 bg-red-600/10 text-red-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-red-600/20 transition-all border border-red-500/20 font-oswald"
                    aria-label="Excluir contatos selecionados"
                  >
                    Excluir Selecionados
                  </button>
                )}
                <button
                  onClick={() => setSelectedContactIds([])}
                  className="px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-white/10 transition-all border border-white/10 font-oswald"
                  aria-label="Cancelar seleção"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleOpenCategoryModal}
                  className="px-6 py-3 bg-white/5 text-blue-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-white/10 transition-all border border-blue-500/20 font-oswald"
                  aria-label="Gerenciar categorias"
                >
                  Categorias
                </button>
                <button
                  onClick={handleNewContact}
                  className="px-8 py-3 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald"
                  aria-label="Criar novo contato"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  + Novo Contato
                </button>
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="p-3 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10 border border-white/10 transition-all"
                    aria-label="Menu de opções"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  {isMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-3 w-56 bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl py-2 z-[3000] overflow-visible">
                        <button
                          onClick={() => {
                            handleOpenCSVImportModal();
                            setIsMenuOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] hover:bg-white/5 hover:text-white transition-all flex items-center gap-3 font-oswald"
                        >
                          <span className="p-1.5 bg-blue-500/10 rounded-lg text-blue-400">📄</span>
                          Importar CSV
                        </button>
                        <button
                          onClick={() => {
                            setIsChatwootSyncModalOpen(true);
                            setIsMenuOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] hover:bg-white/5 hover:text-white transition-all flex items-center gap-3 font-oswald border-t border-white/5"
                        >
                          <span className="p-1.5 bg-green-500/10 rounded-lg text-green-400">💬</span>
                          BChat
                        </button>
                        <button
                          onClick={() => {
                            setIsPerfexImportModalOpen(true);
                            setIsMenuOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] hover:bg-white/5 hover:text-white transition-all flex items-center gap-3 font-oswald border-t border-white/5"
                        >
                          <span className="p-1.5 bg-purple-500/10 rounded-lg text-purple-400">🔧</span>
                          Perfex CRM
                        </button>
                        {(user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') && (
                          <button
                            onClick={() => {
                              setIsMenuOpen(false);
                              showConfirm(
                                'Excluir Todos os Contatos',
                                'Tem certeza que deseja excluir TODOS os contatos deste tenant? Esta ação não pode ser desfeita.',
                                async () => {
                                  try {
                                    const result = await apiService.deleteAllContacts();
                                    toast.success(`${result.deleted} contato(s) excluído(s).`);
                                    refresh();
                                  } catch (error) {
                                    toast.error('Erro ao excluir todos os contatos');
                                  }
                                },
                                'danger'
                              );
                            }}
                            className="w-full px-4 py-3 text-left text-[10px] font-bold text-red-400 uppercase tracking-[0.2em] hover:bg-red-600/10 hover:text-red-300 transition-all flex items-center gap-3 font-oswald border-t border-white/5"
                          >
                            <span className="p-1.5 bg-red-500/10 rounded-lg text-red-400">🗑️</span>
                            Excluir Todos
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        }
      />

      <div className="p-4 space-y-4 max-w-[1600px] mx-auto">

        <SearchAndFilters
          search={search}
          selectedCategory={selectedCategory}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onClearFilters={handleClearFilters}
        />

        {contacts.length > 0 && (
          <div className="flex items-center justify-between mb-2 px-2">
            <button
              onClick={handleSelectAllContacts}
              className="text-[10px] font-bold text-blue-400 hover:text-blue-300 uppercase tracking-[0.2em] font-oswald transition-all flex items-center gap-2 group"
            >
              <span className="w-4 h-4 rounded border border-blue-500/30 flex items-center justify-center group-hover:border-blue-400 transition-all">
                {selectedContactIds.length === contacts.length && <div className="w-2 h-2 bg-blue-400 rounded-sm" />}
              </span>
              {selectedContactIds.length === contacts.length ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>
            {selectedContactIds.length > 0 && (
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">
                {selectedContactIds.length} de {contacts.length} selecionados
              </span>
            )}
          </div>
        )}

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden">
          <ContactList
            contacts={contacts}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteContact}
            selectedContactIds={selectedContactIds}
            onToggleSelect={handleToggleSelectContact}
            onSelectAll={handleSelectAllContacts}
            selectionMode={true}
          />
        </div>

        {totalPages > 1 && (
          <div className="mt-10">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <div className="text-center text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">
          Mostrando <span className="text-blue-400">{contacts.length}</span> de <span className="text-white">{total}</span> contatos
        </div>
      </div>

      {isFormOpen && (
        <ContactForm
          contact={editingContact}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
          onAddCategory={handleOpenCategoryModal}
          categoriesVersion={categoriesVersion}
        />
      )}

      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
      />

      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={handleCloseCSVImportModal}
        onSuccess={handleCSVImportSuccess}
      />

      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={handleCloseBulkEdit}
        selectedContactIds={selectedContactIds}
        onSuccess={handleBulkEditSuccess}
      />

      <ChatwootSyncModal
        isOpen={isChatwootSyncModalOpen}
        onClose={() => setIsChatwootSyncModalOpen(false)}
        onSuccess={() => {
          refresh();
          setIsChatwootSyncModalOpen(false);
        }}
      />

      <PerfexImportModal
        isOpen={isPerfexImportModalOpen}
        onClose={() => setIsPerfexImportModalOpen(false)}
        onSuccess={() => {
          refresh();
          setIsPerfexImportModalOpen(false);
        }}
      />

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-3xl shadow-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
              {confirmDialogConfig.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 font-medium pt-4">
              {confirmDialogConfig.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-8 gap-3">
            <AlertDialogCancel className="flex-1 px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl hover:bg-white/10 transition-all border border-white/10 font-oswald">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                confirmDialogConfig.onConfirm();
                setConfirmDialogOpen(false);
              }}
              className={`flex-1 px-6 py-3 ${confirmDialogConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700 shadow-red-900/40' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-900/40'} text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-lg transition-all font-oswald`}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
